import React, {useContext, useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {editInvoiceSettings, getInvoiceSettingsInfo} from '../../api/api';
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import {Input, Label, Button, FormFeedback} from "reactstrap";
import Loader from "../../components/Loader";

const PADDING_OPTIONS = [
    {value: 3, label: '3 digits (001)'},
    {value: 4, label: '4 digits (0001)'},
    {value: 5, label: '5 digits (00001)'},
    {value: 6, label: '6 digits (000001)'},
];

function InvoiceSettingsTab({ currentTab, setCurrentTab }) {
    const {refreshToken} = useContext(AuthContext);
    const [settings, setSettings] = useState('');
    const [settingId, setSettingId] = useState(null);
    const queryClient = useQueryClient();

    const { refetch } = useQuery({
        queryKey: ['invoice-settings'],
        queryFn: () => getInvoiceSettingsInfo(),
        keepPreviousData: true,
        retry: false,
        onSuccess: (data) => {
            setSettingId(data.results[0].id);
            setSettings(data.results[0]);
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const [fieldErrors, setFieldErrors] = useState({});

    const invoiceSettingMutation = useMutation(editInvoiceSettings, {
        onSuccess: () => {
            setFieldErrors({});
            toast.success('Invoice settings updated successfully!');
            queryClient.invalidateQueries(['invoice-settings']);
        },
        onError: (err) => {
            const data = err?.response?.data;
            if (data && typeof data === 'object' && !data.detail) {
                const errors = {};
                Object.keys(data).forEach(key => {
                    errors[key] = Array.isArray(data[key]) ? data[key][0] : data[key];
                });
                setFieldErrors(errors);
                const firstError = Object.values(errors)[0];
                toast.error(firstError);
            } else {
                toast.error(`An error occurred! ${data?.detail || 'Unknown error'}`);
            }
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        invoiceSettingMutation.mutate({ formData: settings, settingId });
    };

    // Build preview
    const buildPreview = () => {
        const parts = [];
        if (settings.invoice_number_prefix) {
            parts.push(settings.invoice_number_prefix);
        }
        if (String(settings.include_financial_year) === 'true') {
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1;
            let fyStart, fyEnd;
            if (month < 4) {
                fyStart = String(year - 1).slice(2);
                fyEnd = String(year).slice(2);
            } else {
                fyStart = String(year).slice(2);
                fyEnd = String(year + 1).slice(2);
            }
            parts.push(`${fyStart}-${fyEnd}`);
        }
        const padding = parseInt(settings.invoice_number_padding) || 4;
        const nextNum = parseInt(settings.next_invoice_number) || 1;
        parts.push(String(nextNum).padStart(padding, '0'));
        return parts.join('/');
    };

    const buildCnPreview = () => {
        const parts = [];
        if (settings.cn_number_prefix) {
            parts.push(settings.cn_number_prefix);
        }
        if (String(settings.include_financial_year) === 'true') {
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1;
            let fyStart, fyEnd;
            if (month < 4) {
                fyStart = String(year - 1).slice(2);
                fyEnd = String(year).slice(2);
            } else {
                fyStart = String(year).slice(2);
                fyEnd = String(year + 1).slice(2);
            }
            parts.push(`${fyStart}-${fyEnd}`);
        }
        const cnPadding = parseInt(settings.cn_number_padding) || 4;
        const cnNextNum = parseInt(settings.cn_next_number) || 1;
        parts.push(String(cnNextNum).padStart(cnPadding, '0'));
        return parts.join('/');
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                <div className="settings-card ml-3">
                    <div className="card-box">
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Invoice Creation</h6>
                        <div className="form-group">
                            <Label>Auto Create Invoice</Label>
                            <Input
                                type="select"
                                name="auto_create_invoice"
                                className="form-control"
                                value={settings.auto_create_invoice}
                                onChange={handleChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">When disabled, orders can be invoiced manually from the order actions menu.</small>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        {/* -- Invoice Number Format -- */}
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Invoice Number Format</h6>
                        <div className="form-group">
                            <Label>Invoice Number Prefix</Label>
                            <Input
                                type="text"
                                name="invoice_number_prefix"
                                className="form-control"
                                value={settings.invoice_number_prefix || ''}
                                onChange={handleChange}
                                placeholder="e.g. INV"
                                maxLength={5}
                                invalid={!!fieldErrors.invoice_number_prefix}
                            />
                            {fieldErrors.invoice_number_prefix
                                ? <FormFeedback>{fieldErrors.invoice_number_prefix}</FormFeedback>
                                : <small className="text-muted">Max 5 characters. Only letters and numbers allowed.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Include Financial Year</Label>
                            <Input
                                type="select"
                                name="include_financial_year"
                                className="form-control"
                                value={settings.include_financial_year}
                                onChange={handleChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Adds financial year (e.g. 25-26) to invoice number. GST recommended.</small>
                        </div>
                        <div className="form-group">
                            <Label>Next Invoice Number</Label>
                            <Input
                                type="text"
                                name="next_invoice_number"
                                className="form-control"
                                value={settings.next_invoice_number ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        handleChange(e);
                                    }
                                }}
                                invalid={!!fieldErrors.next_invoice_number}
                            />
                            {fieldErrors.next_invoice_number
                                ? <FormFeedback>{fieldErrors.next_invoice_number}</FormFeedback>
                                : <small className="text-muted">The next invoice will use this number.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Number Padding</Label>
                            <Input
                                type="select"
                                name="invoice_number_padding"
                                className="form-control"
                                value={settings.invoice_number_padding || 4}
                                onChange={handleChange}
                                invalid={!!fieldErrors.invoice_number_padding}
                            >
                                {PADDING_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Input>
                            {fieldErrors.invoice_number_padding && <FormFeedback>{fieldErrors.invoice_number_padding}</FormFeedback>}
                        </div>
                        <div className="form-group">
                            <Label>Auto Reset on New Financial Year</Label>
                            <Input
                                type="select"
                                name="auto_reset_financial_year"
                                className="form-control"
                                value={settings.auto_reset_financial_year}
                                onChange={handleChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Automatically reset invoice number to 1 on April 1st every year.</small>
                        </div>
                        <div className="form-group">
                            <Label>Preview</Label>
                            <div className="form-control" style={{backgroundColor: 'transparent', border: '1px solid #444', fontWeight: 600, fontSize: 15}}>
                                {settings ? buildPreview() : '...'}
                            </div>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        {/* -- Invoice Footer -- */}
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Invoice Footer</h6>
                        <div className="form-group">
                            <Label>Notes</Label>
                            <Input
                                type="textarea"
                                name="invoice_notes"
                                className="form-control"
                                value={settings.invoice_notes || ''}
                                onChange={handleChange}
                                rows={3}
                                placeholder="e.g. Thank you for your business!"
                            />
                            <small className="text-muted">Shown at the bottom of every invoice.</small>
                        </div>
                        <div className="form-group">
                            <Label>Terms & Conditions</Label>
                            <Input
                                type="textarea"
                                name="terms_and_conditions"
                                className="form-control"
                                value={settings.terms_and_conditions || ''}
                                onChange={handleChange}
                                rows={3}
                                placeholder="e.g. Goods once sold will not be taken back or exchanged."
                            />
                            <small className="text-muted">Shown at the bottom of every invoice under Terms & Conditions.</small>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        {/* -- Credit Note Number Format -- */}
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Credit Note Number Format</h6>
                        <div className="form-group">
                            <Label>Credit Note Number Prefix</Label>
                            <Input
                                type="text"
                                name="cn_number_prefix"
                                className="form-control"
                                value={settings.cn_number_prefix || ''}
                                onChange={handleChange}
                                placeholder="e.g. CN"
                                maxLength={5}
                                invalid={!!fieldErrors.cn_number_prefix}
                            />
                            {fieldErrors.cn_number_prefix
                                ? <FormFeedback>{fieldErrors.cn_number_prefix}</FormFeedback>
                                : <small className="text-muted">Max 5 characters. Only letters and numbers allowed.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Next Credit Note Number</Label>
                            <Input
                                type="text"
                                name="cn_next_number"
                                className="form-control"
                                value={settings.cn_next_number ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        handleChange(e);
                                    }
                                }}
                                invalid={!!fieldErrors.cn_next_number}
                            />
                            {fieldErrors.cn_next_number
                                ? <FormFeedback>{fieldErrors.cn_next_number}</FormFeedback>
                                : <small className="text-muted">The next credit note will use this number.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Number Padding</Label>
                            <Input
                                type="select"
                                name="cn_number_padding"
                                className="form-control"
                                value={settings.cn_number_padding || 4}
                                onChange={handleChange}
                                invalid={!!fieldErrors.cn_number_padding}
                            >
                                {PADDING_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Input>
                            {fieldErrors.cn_number_padding && <FormFeedback>{fieldErrors.cn_number_padding}</FormFeedback>}
                        </div>
                        <div className="form-group">
                            <Label>Preview</Label>
                            <div className="form-control" style={{backgroundColor: 'transparent', border: '1px solid #444', fontWeight: 600, fontSize: 15}}>
                                {settings ? buildCnPreview() : '...'}
                            </div>
                        </div>

                        <Button type="submit" className="btn bx-btn-primary waves-effect waves-light" disabled={invoiceSettingMutation.isLoading}>
                            {invoiceSettingMutation.isLoading ? <><Loader size={'small'}/>Saving...</> : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
}

export default InvoiceSettingsTab;
